import React, { useState, useEffect } from 'react';
import './About.css';

const About = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check on mount
    checkScreenSize();

    // Add event listener for resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <div className="main-content">
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid-pattern"></div>
          <div className="hero-orbs">
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-orb hero-orb-3"></div>
          </div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            {isMobile ? (
              'About Us'
            ) : (
              <>
                About <span className="text-gradient">FalconDev</span>Team
              </>
            )}
          </h1>
          <p className="hero-subtitle">A passionate team of game developers crafting immersive experiences</p>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Our Story</h2>
              <p>
                FalconDevTeam was born from a shared passion for creating games that not only entertain 
                but also inspire. We believe that every game should tell a story, challenge the player, 
                and leave a lasting impression.
              </p>
              <p>
                Starting with our first project, Storm Dash, created using GDevelop's powerful no-code platform, 
                we're building a foundation of quality, innovation, and player-focused design. Our team brings together diverse skills in 
                programming, design, art, and sound to create cohesive gaming experiences, whether through traditional coding or modern visual development tools.
              </p>
            </div>
            <div className="about-visual">
              <div className="visual-placeholder">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <p>Team Collaboration</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <h2 className="section-title">Our Mission</h2>
          <div className="grid">
            <div className="card">
              <div className="card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3>Innovation</h3>
              <p>Pushing the boundaries of what's possible in game development through creative solutions and cutting-edge technology.</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </div>
              <h3>Passion</h3>
              <p>Every line of code, every pixel, and every sound is crafted with genuine enthusiasm for the art of game development.</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3>Excellence</h3>
              <p>Striving for the highest quality in every aspect of our games, from gameplay mechanics to visual polish.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <h2 className="section-title">Development Process</h2>
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Concept & Design</h3>
                <p>We start with brainstorming sessions, creating concept art, and designing game mechanics that will engage and challenge players.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Development</h3>
                <p>Our programmers bring the designs to life, implementing core mechanics, physics, and game systems with clean, efficient code.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Testing & Polish</h3>
                <p>Rigorous testing ensures smooth gameplay, balanced difficulty, and a polished user experience that feels professional.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Launch & Support</h3>
                <p>We launch our games with ongoing support, updates, and community engagement to ensure long-term success.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <h2 className="section-title">Join Our Community</h2>
          <p className="section-subtitle">Connect with fellow gamers and developers</p>
          <div className="community-links">
            <a href="https://www.tiktok.com/@falcondevteam" target="_blank" rel="noopener noreferrer" className="community-link">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              <span>Follow on TikTok</span>
            </a>
            <a href="https://www.youtube.com/@FalconDevTeam" target="_blank" rel="noopener noreferrer" className="community-link">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span>Subscribe on YouTube</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
